import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Formik, Form } from 'formik';

import CTAButton from '../../shared/CTAButton';
import ColumnInput from '../../shared/ColumnInput';
import Error from '../../shared/Error';
import StickyInput from '../../shared/StickyInput';
import Support from '../../shared/Support';
import useRouteStatus from '../../../hooks/useRouteStatus';
import useSessionEntity from '../../../hooks/useSessionEntity';
import { asyncActions } from '../../../actions/general';
import { emailRegex } from '../../../lib/constants/SiteVariables';
import { notifyOwner } from '../../../actions/sessions';

export default function NotifyOwner({ location }) {
  const dispatch = useDispatch();
  const isLoading = useSelector((state) => state.sessions.get('loading'));
  const application = useSessionEntity('application');
  const [notified, setNotified] = useState(false);
  const [owner, setOwner] = useState({
    first_name: '',
    last_name: '',
    email: '',
  });

  const save = (values) => {
    dispatch(asyncActions(notifyOwner(values)))
      .then((res) => setNotified(res.owner_notified))
      .then((res) => setOwner(values));
  };

  useRouteStatus(location);

  const ownerNotNotified = (
    <div id="content" className="text-center mx-3 sm:mx-auto">
      <div className="my-4">
        <p>That owner already is a Credit Key customer. </p>
        <p>Please contact Credit Key support for further assistance.</p>
      </div>
      <Support />
    </div>
  );

  const ownerNotified = (
    <div id="content" className="text-center mx-3 sm:mx-auto">
      <div className="font-avenir-medium mb-2">
        We've sent an email to {owner.first_name} {owner.last_name} for approval
        and order submission.
      </div>

      <div>
        Once {owner.first_name} completes the process, you will have the ability
        to make future purchases using Credit Key, without the need for
        additional approval.
      </div>
      <Support />
    </div>
  );

  if (owner.email !== '') {
    if (notified) return ownerNotified;
    if (!notified) return ownerNotNotified;
  }

  return (
    <div id="content" className="text-center px-4 mobile:px-0">
      <div className="text-2xl font-avenir-black mb-5 text-navy-blue">
        Owner Express Approval
      </div>
      <div className="mb-2">
        Credit Key needs to approve an owner of your business.
      </div>
      <div className="mb-2">
        We'll need about 5 minutes of your business owner's time to approve this
        order, and get you approved to purchase for {application.company_name}.
      </div>
      <div className="mb-3 font-avenir-medium">
        Please provide the first and last name, and email of an owner of
        <span className="text-primary-blue mx-1">{application.company_name}:</span>
      </div>

      <Error />

      <Formik
        initialValues={owner}
        validate={async (values) => {
          const errors = {};

          if (!values.first_name) errors.first_name = '* Required';
          if (!values.last_name) errors.last_name = '* Required';

          if (!values.email) {
            errors.email = '* Required';
          } else if (!emailRegex.test(values.email)) {
            errors.email = '* Invalid email address';
          }

          return errors;
        }}
        onSubmit={(values, { setSubmitting }) => {
          save(values);
        }}
      >
        {({ isSubmitting, errors, values }) => (
          <Form>
            <ColumnInput error={errors.first_name}>
              <StickyInput id="first_name" placeholder="First Name" values={values} />
            </ColumnInput>

            <ColumnInput error={errors.last_name}>
              <StickyInput id="last_name" placeholder="Last Name" values={values} />
            </ColumnInput>

            <ColumnInput error={errors.email}>
              <StickyInput id="email" type="email" placeholder="Email" values={values} />
            </ColumnInput>

            <CTAButton
              buttonClass="w-3/4 mobile:w-2/3 mx-auto md:w-full my-4 bg-primary-blue font-avenir-medium mobile:font-avenir-black tracking-wider text-white rounded-full normal-case mobile:uppercase py-3 px-6 text-sm md:text-base inline-block hover:bg-navy-blue flex justify-center items-center"
              buttonText="Send Approval Link to Owner"
              disabled={isLoading}
              isLoading={isLoading}
            />
          </Form>
        )}
      </Formik>
      <Support />
    </div>
  );
}
